import { type Dtos, type WorkInProgressScheduleExtractionId } from "@inrev/common";
import {
	createContext,
	type Dispatch,
	type FC,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { type PeriodYearGroup, usePeriodSelection } from "../../patterns/period-selector";
import {
	buildWipAnalysisPeriodSelectorItems,
	defaultWipAnalysisPeriodSelectorExtractionIds,
	groupWipAnalysisPeriodSelectorItemsByYear,
	type WipAnalysisPeriodSelectorItem,
} from "./wipAnalysisPeriodSelection";
import type { WipAnalysisGridPeriodOrder } from "./wipPeriod";

type WipAnalysisPeriodSelectorContextValue = {
	periodYearGroups: PeriodYearGroup<WipAnalysisPeriodSelectorItem>[];
	/** True when any period can be selected, whether or not one is. */
	hasPeriods: boolean;
	selectedExtractionIds: Set<WorkInProgressScheduleExtractionId>;
	toggleItem: (item: WipAnalysisPeriodSelectorItem) => void;
	isItemSelected: (item: WipAnalysisPeriodSelectorItem) => boolean;
	selectedCount: number;
	periodOrder: WipAnalysisGridPeriodOrder;
	setPeriodOrder: Dispatch<SetStateAction<WipAnalysisGridPeriodOrder>>;
};

const WipAnalysisPeriodSelectorContext =
	createContext<WipAnalysisPeriodSelectorContextValue | null>(null);

export const useWipAnalysisPeriodSelector = (): WipAnalysisPeriodSelectorContextValue => {
	const ctx = useContext(WipAnalysisPeriodSelectorContext);
	if (!ctx) {
		throw new Error(
			"useWipAnalysisPeriodSelector must be used inside WipAnalysisPeriodSelectorProvider",
		);
	}
	return ctx;
};

type WipAnalysisPeriodSelectorProviderProps = {
	extractions: ReadonlyArray<Dtos.Wip.Extraction.Get.Response>;
	/**
	 * Decides which extractions start checked: each statement's schedule of record. The defaults
	 * apply only when the extraction list first fills, so pass statements no later than extractions.
	 */
	statements: ReadonlyArray<Dtos.Wip.Statement.Get.Response>;
	children: ReactNode;
};

export const WipAnalysisPeriodSelectorProvider: FC<WipAnalysisPeriodSelectorProviderProps> = ({
	extractions,
	statements,
	children,
}) => {
	const periodSelectorItems = useMemo(
		() => buildWipAnalysisPeriodSelectorItems(extractions, statements),
		[extractions, statements],
	);
	const periodYearGroups = useMemo(
		() => groupWipAnalysisPeriodSelectorItemsByYear(periodSelectorItems),
		[periodSelectorItems],
	);
	const periodSelectorItemIds = useMemo(
		() => periodSelectorItems.map((item) => item.extractionId),
		[periodSelectorItems],
	);
	const defaultSelectedIds = useMemo(
		() => defaultWipAnalysisPeriodSelectorExtractionIds(periodSelectorItems),
		[periodSelectorItems],
	);
	const { selectedIds, toggle, isSelected, selectedCount } = usePeriodSelection(
		periodSelectorItemIds,
		defaultSelectedIds,
	);

	const toggleItem = useCallback(
		(item: WipAnalysisPeriodSelectorItem) => toggle(item.extractionId),
		[toggle],
	);
	const isItemSelected = useCallback(
		(item: WipAnalysisPeriodSelectorItem) => isSelected(item.extractionId),
		[isSelected],
	);

	const [periodOrder, setPeriodOrder] = useState<WipAnalysisGridPeriodOrder>("newest-first");

	const value = useMemo(
		() => ({
			periodYearGroups,
			hasPeriods: periodSelectorItems.length > 0,
			selectedExtractionIds: selectedIds,
			toggleItem,
			isItemSelected,
			selectedCount,
			periodOrder,
			setPeriodOrder,
		}),
		[
			periodYearGroups,
			periodSelectorItems.length,
			selectedIds,
			toggleItem,
			isItemSelected,
			selectedCount,
			periodOrder,
		],
	);

	return (
		<WipAnalysisPeriodSelectorContext value={value}>{children}</WipAnalysisPeriodSelectorContext>
	);
};
